// Inside the "3 steps" guide, under step 2 (add a post): asks once whether the customer is a real-estate
// agent (unless the business is already known), and offers agents to import their yad2
// listings as posts — the same agent search and import as the yad2 signup page
// (Yad2Register.tsx). Picking the agent starts the import (API: /user/onboarding-guide/yad2).
import React, {useEffect, useState} from 'react'
import axios from 'axios'

const API_URL = process.env.REACT_APP_API_URL

type Agent = {
  broker_id: number
  name: string
  phone: string
  agency_name?: string
  listings_count?: number
  avatar?: string
}

export type Yad2Status = {
  ask_real_estate?: boolean
  real_estate?: boolean
  yad2?: {enabled: boolean; agent_name: string; importing: boolean; dismissed: boolean}
}

// While true, step 2 shows only this box (no video / "create post" button): the real-estate
// question, then "do you have listings on yad2?" (yes → search + import), the import status.
// "No" on either question → the regular add-a-post path.
export const yad2Blocks = (status: Yad2Status) =>
  !!status.ask_real_estate || (!!status.real_estate && !!status.yad2 && !status.yad2.dismissed)

export default function Yad2Box({
  status,
  reload,
  track,
}: {
  status: Yad2Status
  reload: () => void
  track: (step: string, event: string) => void
}) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<Agent[]>([])
  const [searching, setSearching] = useState(false)
  const [agent, setAgent] = useState<Agent | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [hasListings, setHasListings] = useState(false)
  const y = status.yad2

  useEffect(() => {
    if (agent || query.trim().length < 2) {
      setResults([])
      return
    }
    setSearching(true)
    const t = setTimeout(async () => {
      try {
        const {data} = await axios.get(`${API_URL}/public/yad2-agents/autocomplete`, {
          params: {q: query.trim(), limit: 6},
        })
        setResults(data?.results || [])
      } catch {
        setResults([])
      }
      setSearching(false)
    }, 300)
    return () => clearTimeout(t)
  }, [query, agent])

  const post = async (url: string, body: any) => {
    setBusy(true)
    setError('')
    try {
      await axios.post(`${API_URL}${url}`, body)
      reload()
    } catch (e: any) {
      setError(e?.response?.data?.error || 'משהו השתבש, נסו שוב')
    }
    setBusy(false)
  }

  if (status.ask_real_estate) {
    return (
      <div className='og-yad2'>
        <b>🏠 אתה סוכן נדל״ן?</b>
        <small>סוכנים יכולים לייבא את הנכסים שלהם מיד2 כפוסטים מוכנים, בלחיצה אחת.</small>
        <div className='og-yad2-row'>
          <button
            className='btn btn-sm btn-primary'
            disabled={busy}
            onClick={() => {
              track('guide', 're_yes')
              post('/user/onboarding-guide/real-estate', {yes: true})
            }}
          >
            כן, אני סוכן נדל״ן
          </button>
          <button
            className='btn btn-sm btn-light'
            disabled={busy}
            onClick={() => {
              track('guide', 're_no')
              post('/user/onboarding-guide/real-estate', {yes: false})
            }}
          >
            לא
          </button>
        </div>
      </div>
    )
  }

  if (!status.real_estate || !y || y.dismissed) return null

  if (y.enabled) {
    return (
      <div className='og-yad2 og-yad2-ok'>
        {y.importing ? (
          <>
            <b>⏳ מייבאים את הנכסים שלך מיד2…</b>
            <small>
              {y.agent_name ? `${y.agent_name}: ` : ''}הנכסים יופיעו כפוסטים בקמפיין תוך כמה שניות.
            </small>
          </>
        ) : (
          <>
            <b>✅ הנכסים שלך מיד2 יובאו</b>
            <small>אפשר לראות ולערוך אותם בעמוד הקמפיינים.</small>
          </>
        )}
      </div>
    )
  }

  if (!hasListings) {
    return (
      <div className='og-yad2'>
        <b>📥 יש לך נכסים ביד2?</b>
        <small>
          אם כן — אין צורך להעלות פוסטים: נייבא את כל הנכסים שלך כפוסטים מוכנים, עם התמונות
          והפרטים.
        </small>
        {error && <small className='og-yad2-error'>{error}</small>}
        <div className='og-yad2-row'>
          <button
            className='btn btn-sm btn-primary'
            disabled={busy}
            onClick={() => {
              track('guide', 'yad2_yes')
              setHasListings(true)
            }}
          >
            כן, יש לי נכסים ביד2
          </button>
          <button
            className='btn btn-sm btn-light'
            disabled={busy}
            onClick={() => {
              track('guide', 'yad2_skip')
              post('/user/onboarding-guide/yad2', {skip: true})
            }}
          >
            לא
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className='og-yad2'>
      <b>📥 ייבוא הנכסים שלך מיד2</b>
      <small>מחפשים את עצמך ובוחרים מהרשימה.</small>
      {agent ? (
        <div className='og-yad2-agent'>
          {agent.avatar && <img src={agent.avatar} alt='' />}
          <span>
            <b>{agent.name}</b>
            {agent.agency_name && <small>{agent.agency_name}</small>}
            <small>{agent.listings_count || 0} נכסים ביד2</small>
          </span>
          <button className='btn btn-sm btn-link' onClick={() => setAgent(null)}>
            החלפה
          </button>
        </div>
      ) : (
        <>
          <input
            className='form-control form-control-sm'
            placeholder='השם שלך, או הטלפון שמופיע במודעות שלך ביד2'
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <small className='og-yad2-hint'>
            📞 חיפוש לפי טלפון: המספר שמופיע במודעות שלך ביד2 — בדרך כלל מספר וירטואלי של יד2, לא
            הנייד האישי.
          </small>
          {results.length > 0 && (
            <div className='og-yad2-results'>
              {results.map((a) => (
                <button
                  key={a.broker_id}
                  onClick={() => {
                    setAgent(a)
                    setQuery(a.name || a.phone)
                    track('guide', 'yad2_pick')
                  }}
                >
                  <b>{a.name}</b>
                  <small>
                    {[a.agency_name, `${a.listings_count || 0} נכסים`].filter(Boolean).join(' · ')}
                  </small>
                </button>
              ))}
            </div>
          )}
          {!searching && query.trim().length >= 2 && results.length === 0 && (
            <small className='og-yad2-empty'>לא מצאנו סוכן בשם הזה ביד2</small>
          )}
        </>
      )}
      {error && <small className='og-yad2-error'>{error}</small>}
      <div className='og-yad2-row'>
        {agent && (
          <button
            className='btn btn-sm btn-primary'
            disabled={busy}
            onClick={() => {
              track('guide', 'yad2_import')
              post('/user/onboarding-guide/yad2', {broker_id: agent.broker_id})
            }}
          >
            {busy ? 'מתחילים…' : `ייבוא הנכסים${agent.listings_count ? ` (${agent.listings_count})` : ''}`}
          </button>
        )}
        <button
          className='btn btn-sm btn-light'
          disabled={busy}
          onClick={() => {
            setAgent(null)
            setQuery('')
            setHasListings(false)
          }}
        >
          חזרה
        </button>
      </div>
    </div>
  )
}
